import { useMutation } from '@tanstack/react-query';
import { SKIP_FORBIDDEN_HEADER, SKIP_UNAUTH_HEADER } from '@sentinel/api-client';
import { useApi } from '../../lib/api';

export interface ConfirmResetPayload {
  token: string;
  new_password: string;
}

export interface ConfirmResetResult {
  message: string;
  redirect_url?: string;
}

export function useConfirmReset() {
  const api = useApi();
  return useMutation({
    mutationFn: async ({
      token,
      new_password,
    }: ConfirmResetPayload): Promise<ConfirmResetResult> => {
      // This is an unauthenticated flow that owns its own error surface, so it
      // skips both global handlers — the IP-policy 403 belongs on the reset
      // screen, not in a permission toast.
      const { data, error, response } = (await api.POST('/auth/password/reset-confirm' as never, {
        body: { token, new_password },
        headers: { [SKIP_UNAUTH_HEADER]: '1', [SKIP_FORBIDDEN_HEADER]: '1' },
      } as never)) as { data?: unknown; error?: unknown; response: Response };
      if (error) {
        const err = new Error('reset_confirm_failed') as Error & {
          status?: number;
          body?: unknown;
        };
        err.status = response?.status;
        err.body = error;
        throw err;
      }
      return data as ConfirmResetResult;
    },
  });
}
